import { assetAmountInEth, getAssetInfo, getAssetInfoByAddress } from '@defisaver/tokens';
import {
  Network,
  networkIdToLabel,
} from '../../services/utils';

type AutomationSide = 'supplied' | 'borrowed';
type AutomationTableTone = 'neutral' | 'safe' | 'near' | 'trigger' | 'boost' | 'disabled';

export type AutomationSortValue = number | string | undefined;

export interface AutomationStrategyColumn {
  key: string;
  label: string;
  numeric?: boolean;
  sortable?: boolean;
}

// Marks a substring of a line's text as a full value worth copying - used when a token
// symbol could not be resolved and the shortened address is shown in its place.
export interface AutomationTableCopy {
  label: string;
  value: string;
}

export interface AutomationTableLine {
  text: string;
  meta?: string;
  tone?: AutomationTableTone;
  copy?: AutomationTableCopy[];
}

export interface AutomationStrategyRow {
  cells: Record<string, AutomationTableLine[]>;
  // Raw comparable values behind each rendered cell. Sorting never touches the formatted
  // strings, which carry units, prefixes and thousands separators.
  sortValues: Record<string, AutomationSortValue>;
}

export interface AutomationPositionAsset {
  symbol: string;
  amount: string;
  usdValue?: string;
  side: AutomationSide;
}

type TokenMap = Record<string, string>;

interface RatioState {
  text: string;
  meta?: string;
  tone: AutomationTableTone;
}

const MAX_UINT_DIGITS = 50;
const NO_GAS_CAP_THRESHOLD = 1_000_000_000;

const isPresent = (value: unknown): value is string | number => value !== undefined
  && value !== null
  && value !== '';

const toNumber = (value: unknown): number | undefined => {
  if (!isPresent(value)) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const trimNumber = (number: number, maximumFractionDigits: number): string => number.toLocaleString('en-US', {
  maximumFractionDigits,
  useGrouping: true,
});

const formatAmount = (value: unknown): string => {
  const number = toNumber(value);
  if (number === undefined) return isPresent(value) ? String(value) : '—';
  const absolute = Math.abs(number);
  if (absolute >= 1_000_000_000) return `${trimNumber(number / 1_000_000_000, 2)}B`;
  if (absolute >= 1_000_000) return `${trimNumber(number / 1_000_000, 2)}M`;
  if (absolute >= 1_000) return trimNumber(number, 2);
  if (absolute > 0 && absolute < 0.0001) return trimNumber(number, 8);
  return trimNumber(number, 4);
};

const formatUsd = (value: unknown): string => {
  const number = toNumber(value);
  if (number === undefined) return '—';
  if (Math.abs(number) >= 1_000_000_000) return `$${trimNumber(number / 1_000_000_000, 2)}B`;
  if (Math.abs(number) >= 1_000_000) return `$${trimNumber(number / 1_000_000, 2)}M`;
  return `$${trimNumber(number, 2)}`;
};

const formatRatio = (value: unknown): string => {
  const number = toNumber(value);
  return number === undefined ? '—' : `${trimNumber(number, 2)}%`;
};

const formatPrice = (value: unknown): string => {
  const number = toNumber(value);
  if (number === undefined) return isPresent(value) ? String(value) : '—';
  const absolute = Math.abs(number);
  if (absolute > 0 && absolute < 0.0001) return trimNumber(number, 8);
  if (absolute < 1) return trimNumber(number, 6);
  if (absolute < 100) return trimNumber(number, 4);
  return trimNumber(number, 2);
};

const formatRawInteger = (value: unknown): string => {
  const raw = String(value ?? '');
  if (!raw) return '—';
  if (!/^-?\d+$/.test(raw)) return raw;
  const sign = raw.startsWith('-') ? '-' : '';
  const digits = sign ? raw.slice(1) : raw;
  return `${sign}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const shortenIdentifier = (identifier: unknown): string => {
  const value = String(identifier ?? '');
  if (!value) return 'Unknown account';
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
};

const isAddress = (value: unknown): value is string => typeof value === 'string'
  && /^0x[0-9a-f]{40}$/i.test(value);

const tokenKey = (value: unknown): string => String(value ?? '').toLowerCase();

// getAssetInfoByAddress returns a "?" sentinel for an address it doesn't recognise, and the
// API stores that sentinel straight into positionInfo. Treat it as unresolved so callers fall
// back to the address itself rather than rendering a bare "?".
const knownSymbol = (value: unknown): string | undefined => {
  const symbol = typeof value === 'string' ? value.trim() : '';
  return symbol && symbol !== '?' ? symbol : undefined;
};

const positionAssetEntries = (subscription: any): Array<[string, any]> => Object.entries(
  subscription?.positionInfo?.usedAssets || {},
);

export const getPositionAssets = (
  subscription: any,
  side: AutomationSide,
): AutomationPositionAsset[] => {
  const supplied = side === 'supplied';
  return positionAssetEntries(subscription)
    .filter(([, asset]) => {
      const active = supplied ? asset?.isSupplied : asset?.isBorrowed;
      const amount = supplied
        ? (asset?.supplied ?? asset?.collateral)
        : (asset?.borrowed ?? asset?.debt ?? asset?.drawn);
      const numericAmount = toNumber(amount);
      return Boolean(active) && (numericAmount === undefined || numericAmount !== 0);
    })
    .map(([key, asset]) => {
      const amount = supplied
        ? (asset?.supplied ?? asset?.collateral ?? 0)
        : (asset?.borrowed ?? asset?.debt ?? asset?.drawn ?? 0);
      const usdValue = supplied
        ? asset?.suppliedUsd
        : (asset?.borrowedUsd ?? asset?.drawnUsd);
      return {
        symbol: asset?.symbol || key,
        amount: String(amount),
        usdValue: isPresent(usdValue) ? String(usdValue) : undefined,
        side,
      };
    });
};

interface ResolvedToken {
  symbol: string;
  // Set only when the symbol shown is a shortened address rather than a real ticker.
  address?: string;
}

export type TokenPriceIndex = Record<string, number>;

// ETH and WETH are interchangeable in price terms; nothing else is aliased, since
// wrapped/staked variants (stETH vs wstETH, cbBTC vs WBTC) genuinely differ.
const PRICE_ALIASES: Record<string, string[]> = {
  ETH: ['WETH'],
  WETH: ['ETH'],
};

const lookupPrice = (index: TokenPriceIndex, symbol: string): number | undefined => {
  const key = String(symbol || '').toUpperCase();
  if (!key) return undefined;
  if (index[key] !== undefined) return index[key];
  const alias = (PRICE_ALIASES[key] || []).find(name => index[name] !== undefined);
  return alias === undefined ? undefined : index[alias];
};

// Every position reports both a token amount and its USD value, so the payload is full of
// price samples. Pooling them across all subscriptions prices tokens whose own row carries
// none - a limit order buying WBTC, say, priced from any WBTC lending position. Samples are
// value-weighted, so dust positions can't drag a token's price around.
export const buildTokenPriceIndex = (subscriptions: any[]): TokenPriceIndex => {
  const totals: Record<string, { amount: number; usd: number }> = {};

  (subscriptions || []).forEach((subscription) => {
    const sides: AutomationSide[] = ['supplied', 'borrowed'];
    sides.forEach((side) => {
      // eslint-disable-next-line no-use-before-define
      getPositionAssets(subscription, side).forEach((asset) => {
        const amount = toNumber(asset.amount);
        const usd = toNumber(asset.usdValue);
        if (amount === undefined || usd === undefined || amount <= 0 || usd <= 0) return;
        const key = String(asset.symbol || '').toUpperCase();
        if (!key) return;
        const entry = totals[key] || { amount: 0, usd: 0 };
        entry.amount += amount;
        entry.usd += usd;
        totals[key] = entry;
      });
    });
  });

  return Object.entries(totals).reduce<TokenPriceIndex>((index, [symbol, total]) => {
    // eslint-disable-next-line no-param-reassign
    if (total.amount > 0) index[symbol] = total.usd / total.amount;
    return index;
  }, {});
};

const assignToken = (map: TokenMap, reference: unknown, symbol: unknown) => {
  const resolved = knownSymbol(symbol);
  if (!isAddress(reference) || !resolved) return;
  // eslint-disable-next-line no-param-reassign
  map[tokenKey(reference)] = resolved;
};

const findAssetSymbolById = (subscription: any, id: unknown): string | undefined => {
  if (!isPresent(id)) return undefined;
  const entries = positionAssetEntries(subscription);
  const match = entries.find(([, asset]) => String(asset?.reserveId) === String(id))
    || entries.find(([, asset]) => String(asset?.assetId) === String(id));
  return match ? (match[1]?.symbol || match[0]) : undefined;
};

const buildTokenMap = (
  subscription: any,
  suppliedAssets: AutomationPositionAsset[],
  borrowedAssets: AutomationPositionAsset[],
): TokenMap => {
  const map: TokenMap = {};
  const specific = subscription?.specific || {};
  const subData = subscription?.strategyData?.decoded?.subData || {};
  const triggerData = subscription?.strategyData?.decoded?.triggerData || {};
  const soleSupplied = suppliedAssets.length === 1 ? suppliedAssets[0].symbol : undefined;
  const soleBorrowed = borrowedAssets.length === 1 ? borrowedAssets[0].symbol : undefined;

  assignToken(map, subData.fromToken, subscription?.positionInfo?.fromToken);
  assignToken(map, subData.toToken, subscription?.positionInfo?.toToken);

  [specific.collAsset, specific.collToken, subData.collAsset, subData.collToken, triggerData.collToken]
    .forEach(reference => assignToken(map, reference, soleSupplied));
  [specific.debtAsset, subData.debtAsset, triggerData.debtToken]
    .forEach(reference => assignToken(map, reference, soleBorrowed));

  if (isPresent(specific.collToken) || isPresent(subData.collToken)) {
    assignToken(map, specific.baseToken || subData.baseToken, soleBorrowed);
  }

  assignToken(map, subData.fromAsset, findAssetSymbolById(subscription, subData.fromAssetId));
  assignToken(map, subData.toAsset, findAssetSymbolById(subscription, subData.toAssetId));
  if (suppliedAssets.length === 1) assignToken(map, subData.fromAsset, soleSupplied);

  if (subscription?.positionInfo?.asset) {
    assignToken(map, specific.closeToAssetAddr, subscription.positionInfo.asset);
    assignToken(map, subData.closeToAssetAddr, subscription.positionInfo.asset);
    assignToken(map, triggerData.tokenAddr, subscription.positionInfo.asset);
  }

  return map;
};

const resolveToken = (map: TokenMap, reference: unknown, fallback = 'token', chainId?: unknown): string => {
  if (!isPresent(reference)) return fallback;
  const mapped = map[tokenKey(reference)];
  if (mapped) return mapped;
  if (isAddress(reference)) {
    const known = getAssetInfoByAddress(reference, toNumber(chainId));
    if (known.symbol !== '?') return known.symbol;
    return shortenIdentifier(reference);
  }
  return String(reference);
};

const resolveAssetReference = (
  subscription: any,
  map: TokenMap,
  reference: unknown,
  id: unknown,
  fallback: string,
): string => findAssetSymbolById(subscription, id)
  || resolveToken(map, isPresent(reference) ? reference : id, fallback, subscription?.chainId);

// Prefers the symbol the API stored, then the token map, then the on-chain address itself.
const tokenRef = (
  map: TokenMap,
  storedSymbol: unknown,
  reference: unknown,
  fallback: string,
  chainId: unknown,
): ResolvedToken => {
  const stored = knownSymbol(storedSymbol);
  if (stored) return { symbol: stored };
  const symbol = resolveToken(map, reference, fallback, chainId);
  return isAddress(reference) && symbol === shortenIdentifier(reference)
    ? { symbol, address: reference }
    : { symbol };
};

const copyFor = (...tokens: ResolvedToken[]): AutomationTableCopy[] | undefined => {
  const entries = tokens
    .filter((token): token is Required<ResolvedToken> => Boolean(token.address))
    .map(token => ({ label: token.symbol, value: token.address }));
  return entries.length > 0 ? entries : undefined;
};

const pairText = (base: string, quote?: string): string => (quote ? `${base}/${quote}` : base);

const priceDirection = (state: unknown): string => (Number(state) === 0 ? 'At or above' : 'At or below');

const priceConditionLine = (price: unknown, state: unknown, meta = 'Trigger price'): AutomationTableLine => ({
  text: `${priceDirection(state)} ${formatPrice(price)}`,
  meta,
});

const isStableSymbol = (symbol: string): boolean => getAssetInfo(symbol)?.isStable === true;

interface StablePricePair {
  base: string;
  quote: string;
  inverted: boolean;
}

// A trigger price is denominated "quote per base". When the stablecoin is the base leg that
// reads as a tiny fraction (0.000018 WBTC per USDC), so the pair is flipped and every price
// quoted on it inverted. Pairs already quoted in a stablecoin, and pairs with no stablecoin
// leg at all, are left exactly as the protocol stored them.
const stablePair = (base: string, quote: string, price?: unknown): StablePricePair => {
  // With a price that cannot be inverted, flipping the labels would misstate the direction.
  const numeric = price === undefined ? undefined : toNumber(price);
  const invertible = price === undefined || (numeric !== undefined && numeric > 0);
  return invertible && !isStableSymbol(quote) && isStableSymbol(base)
    ? { base: quote, quote: base, inverted: true }
    : { base, quote, inverted: false };
};

const pairLabel = (pair: StablePricePair): string => pairText(pair.base, pair.quote);

// The trigger reads "quote per base", so the comparable current price is the base token's
// price expressed in the quote token. A stablecoin quote is worth 1; anything else is
// converted through the shared price index.
const pairCurrentPrice = (
  pair: StablePricePair,
  ownBasePriceUsd: number | undefined,
  index: TokenPriceIndex,
): number | undefined => {
  const baseUsd = ownBasePriceUsd ?? lookupPrice(index, pair.base);
  const quoteUsd = isStableSymbol(pair.quote) ? 1 : lookupPrice(index, pair.quote);
  if (baseUsd === undefined || quoteUsd === undefined || quoteUsd <= 0) return undefined;
  return baseUsd / quoteUsd;
};

const priceOnPair = (pair: StablePricePair, price: unknown): unknown => {
  if (!pair.inverted) return price;
  const numeric = toNumber(price);
  return numeric !== undefined && numeric > 0 ? 1 / numeric : price;
};

// Inverting a ratio reverses which way it crosses the threshold.
const stateOnPair = (pair: StablePricePair, state: unknown): unknown => (
  pair.inverted ? (Number(state) === 0 ? 1 : 0) : state
);

// How far the current price has to travel to satisfy a trigger. Both figures are already
// expressed on the same pair, so the percentage is directly meaningful.
const NEAR_TRIGGER_PERCENT = 5;

interface TriggerChange {
  line: AutomationTableLine;
  distance?: number;
}

const changeToTrigger = (
  currentPrice: number | undefined,
  triggerPrice: unknown,
  state: unknown,
  label?: string,
): TriggerChange => {
  const trigger = toNumber(triggerPrice);
  if (currentPrice === undefined || currentPrice <= 0 || trigger === undefined || trigger <= 0) {
    return { line: { text: '\u2014', meta: label } };
  }
  const reached = Number(state) === 0 ? currentPrice >= trigger : currentPrice <= trigger;
  if (reached) {
    return { line: { text: 'Reached', meta: label, tone: 'trigger' }, distance: 0 };
  }
  const change = ((trigger - currentPrice) / currentPrice) * 100;
  const distance = Math.abs(change);
  return {
    line: {
      text: `${change > 0 ? '+' : ''}${trimNumber(change, 2)}%`,
      meta: label ?? (change > 0 ? 'Price must rise' : 'Price must fall'),
      tone: distance < NEAR_TRIGGER_PERCENT ? 'near' : undefined,
    },
    distance,
  };
};

const applyChangeToTrigger = (
  cells: Record<string, AutomationTableLine[]>,
  sortValues: Record<string, AutomationSortValue>,
  changes: TriggerChange[],
) => {
  const distances = changes
    .map(change => change.distance)
    .filter((distance): distance is number => distance !== undefined);
  /* eslint-disable no-param-reassign */
  cells.changeToTrigger = changes.map(change => change.line);
  // Closest trigger wins, so an ascending sort surfaces whatever fires next.
  sortValues.changeToTrigger = distances.length > 0 ? Math.min(...distances) : undefined;
  /* eslint-enable no-param-reassign */
};

const pairConditionLine = (
  pair: StablePricePair,
  price: unknown,
  state: unknown,
  meta = 'Trigger price',
): AutomationTableLine => priceConditionLine(
  priceOnPair(pair, price),
  stateOnPair(pair, state),
  meta,
);

// Some protocols (e.g. MakerDAO) don't report a per-asset USD value; when the asset
// is the only one on its side, the position's side total is that asset's value.
const assetUnitPrice = (
  asset: AutomationPositionAsset | undefined,
  sideAssetCount: number,
  sideTotalUsd: unknown,
): number | undefined => {
  if (!asset) return undefined;
  const amount = toNumber(asset.amount);
  if (!amount || amount <= 0) return undefined;
  const usdValue = toNumber(asset.usdValue)
    ?? (sideAssetCount === 1 ? toNumber(sideTotalUsd) : undefined);
  if (usdValue === undefined) return undefined;
  return usdValue / amount;
};

const currentAssetPrice = (
  state: unknown,
  suppliedAssets: AutomationPositionAsset[],
  borrowedAssets: AutomationPositionAsset[],
  suppliedTotalUsd: unknown,
  borrowedTotalUsd: unknown,
): number | undefined => {
  const isLong = Number(state) === 0;
  const assets = isLong ? suppliedAssets : borrowedAssets;
  const asset = assets.find(a => !isStableSymbol(a.symbol));
  return assetUnitPrice(asset, assets.length, isLong ? suppliedTotalUsd : borrowedTotalUsd);
};

// Unlike take-profit/stop-loss, the price-trigger direction (ratioState) here is a free
// choice independent of which side is risky — so pick whichever side is actually non-stable
// instead of inferring it from state. The collateral (supplied) side is checked first since
// it's always the trigger's base token.
const nonStableAssetPrice = (
  suppliedAssets: AutomationPositionAsset[],
  borrowedAssets: AutomationPositionAsset[],
  suppliedTotalUsd: unknown,
  borrowedTotalUsd: unknown,
): number | undefined => {
  const suppliedMatch = suppliedAssets.find(a => !isStableSymbol(a.symbol));
  if (suppliedMatch) return assetUnitPrice(suppliedMatch, suppliedAssets.length, suppliedTotalUsd);
  const borrowedMatch = borrowedAssets.find(a => !isStableSymbol(a.symbol));
  return assetUnitPrice(borrowedMatch, borrowedAssets.length, borrowedTotalUsd);
};

const statusForRatio = (
  currentValue: unknown,
  triggerValue: unknown,
  direction: 'under' | 'over',
): RatioState => {
  const current = toNumber(currentValue);
  const trigger = toNumber(triggerValue);
  if (current === undefined || current <= 0 || trigger === undefined) {
    return { text: 'Status unavailable', tone: 'neutral' };
  }
  const reached = direction === 'under' ? current <= trigger : current >= trigger;
  if (reached) return { text: direction === 'under' ? 'Trigger reached' : 'Boost reached', tone: direction === 'under' ? 'trigger' : 'boost' };
  return { text: 'Safe', tone: 'safe' };
};

const healthForSubscription = (subscription: any): RatioState => {
  const strategyId = String(subscription?.strategy?.strategyId || '');
  const specific = subscription?.specific || {};
  const triggerData = subscription?.strategyData?.decoded?.triggerData || {};
  const current = toNumber(subscription?.positionInfo?.ratio);
  if (current === undefined || current <= 0) return { text: '', tone: 'neutral' };

  let repay: number | undefined;
  let boost: number | undefined;
  if (['legacy', 'leverage-management', 'leverage-management-eoa'].includes(strategyId)) {
    repay = specific.repayEnabled ? toNumber(specific.triggerRepayRatio) : undefined;
    boost = specific.boostEnabled ? toNumber(specific.triggerBoostRatio) : undefined;
  } else if (['liquidation-protection', 'liquidation-protection-eoa'].includes(strategyId)) {
    repay = toNumber(specific.triggerRepayRatio);
  } else if (strategyId === 'payback') {
    repay = toNumber(specific.triggerRatio ?? triggerData.ratio);
  }

  if (repay !== undefined && current <= repay) return { text: 'Trigger reached', tone: 'trigger' };
  if (boost !== undefined && current >= boost) return { text: 'Boost reached', tone: 'boost' };
  const distances = [
    repay === undefined ? undefined : current - repay,
    boost === undefined ? undefined : boost - current,
  ].filter((distance): distance is number => distance !== undefined && distance >= 0);
  if (distances.length > 0) {
    const distance = Math.min(...distances);
    return { text: distance < 15 ? 'Near trigger' : 'Safe', tone: distance < 15 ? 'near' : 'safe' };
  }
  return { text: 'Position ratio', tone: 'neutral' };
};

const currentRatioLines = (subscription: any): AutomationTableLine[] => {
  const ratio = toNumber(subscription?.positionInfo?.ratio);
  if (ratio === undefined || ratio <= 0) return [{ text: '—' }];
  const health = healthForSubscription(subscription);
  return [{
    text: formatRatio(ratio),
    meta: health.text || undefined,
    tone: health.tone,
  }];
};

const assetLines = (
  assets: AutomationPositionAsset[],
  totalValue: unknown,
  emptyText: string,
  totalLabel: string,
): AutomationTableLine[] => {
  const lines: AutomationTableLine[] = assets.length > 0
    ? assets.map(asset => ({
      text: `${formatAmount(asset.amount)} ${asset.symbol}`,
      meta: assets.length > 1 && isPresent(asset.usdValue) ? formatUsd(asset.usdValue) : undefined,
    }))
    : [{ text: emptyText }];
  if (isPresent(totalValue) && toNumber(totalValue) !== undefined) {
    lines.push({ text: formatUsd(totalValue), meta: totalLabel });
  }
  return lines;
};

const identityLines = (subscription: any): AutomationTableLine[] => {
  const positionInfo = subscription?.positionInfo || {};
  const subData = subscription?.strategyData?.decoded?.subData || {};
  const strategyId = String(subscription?.strategy?.strategyId || '');
  if (isPresent(positionInfo.cdpId)) return [{ text: `#${positionInfo.cdpId}`, meta: 'CDP' }];
  if (isPresent(positionInfo.bondId)) return [{ text: `#${positionInfo.bondId}`, meta: 'Bond' }];
  const troveId = subscription?.specific?.troveId ?? subData.troveId;
  if (isPresent(troveId)) return [{ text: shortenIdentifier(troveId), meta: 'Trove' }];
  return [{
    text: shortenIdentifier(subscription?.owner),
    meta: strategyId.toLowerCase().includes('eoa') ? 'EOA' : 'Smart Wallet',
  }];
};

const protocolLines = (subscription: any): AutomationTableLine[] => [{
  text: subscription?.protocol?.fullName || subscription?.protocol?.name || 'Unknown protocol',
}];

const networkLines = (subscription: any): AutomationTableLine[] => {
  const chainId = toNumber(subscription?.chainId);
  if (chainId === undefined) return [{ text: 'Unknown network' }];
  const label = networkIdToLabel(chainId as Network);
  return [{ text: label || `Chain ${chainId}` }];
};

const formatInterval = (value: unknown): string => {
  const interval = toNumber(value);
  if (!interval || interval <= 0) return '—';
  const units: Array<[number, string]> = [
    [604800, 'week'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
  ];
  const unit = units.find(([seconds]) => interval % seconds === 0);
  if (!unit) return `Every ${formatAmount(interval)} seconds`;
  const count = interval / unit[0];
  return count === 1 ? `Every ${unit[1]}` : `Every ${formatAmount(count)} ${unit[1]}s`;
};

const formatUtcTimestamp = (value: unknown): string => {
  const timestamp = toNumber(value);
  if (timestamp === undefined) return '—';
  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.valueOf())) return '—';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())} UTC`;
};

const nextCadence = (startValue: unknown, intervalValue: unknown, nowMs: number): number | undefined => {
  const start = toNumber(startValue);
  const interval = toNumber(intervalValue);
  if (start === undefined || interval === undefined || interval <= 0) return undefined;
  const now = Math.floor(nowMs / 1000);
  if (start > now) return start;
  return start + ((Math.floor((now - start) / interval) + 1) * interval);
};

const isMaxUintLike = (value: unknown): boolean => {
  if (!isPresent(value)) return false;
  return String(value).replace(/^0+/, '').length >= MAX_UINT_DIGITS;
};

const gasPriceLine = (value: unknown): AutomationTableLine => {
  if (!isPresent(value)) return { text: 'Gas-price condition not returned', meta: 'AND the price condition' };
  const numeric = toNumber(value);
  const sentinel = isMaxUintLike(value) || (numeric !== undefined && numeric > NO_GAS_CAP_THRESHOLD);
  return {
    text: sentinel ? 'No gas-price cap' : `Gas price ≤ ${formatAmount(value)} Gwei`,
    meta: 'AND the price condition',
  };
};

const isConfiguredPrice = (value: unknown): boolean => {
  const numeric = toNumber(value);
  return numeric !== undefined && numeric > 0;
};

const closeDestination = (value: unknown): string => (Number(value) === 0 ? 'Collateral' : 'Debt');

const closeTypes = (value: unknown): { takeProfit?: string; stopLoss?: string } => {
  const closeType = Number(value);
  const takeProfit: Record<number, string> = {
    0: 'Collateral',
    2: 'Debt',
    4: 'Collateral',
    5: 'Collateral',
    6: 'Debt',
    7: 'Debt',
  };
  const stopLoss: Record<number, string> = {
    1: 'Collateral',
    3: 'Debt',
    4: 'Collateral',
    5: 'Debt',
    6: 'Debt',
    7: 'Collateral',
  };
  return { takeProfit: takeProfit[closeType], stopLoss: stopLoss[closeType] };
};

const identitySortValue = (subscription: any): AutomationSortValue => {
  const positionInfo = subscription?.positionInfo || {};
  const subData = subscription?.strategyData?.decoded?.subData || {};
  const cdpId = toNumber(positionInfo.cdpId);
  if (cdpId !== undefined) return cdpId;
  const bondId = toNumber(positionInfo.bondId);
  if (bondId !== undefined) return bondId;
  const troveId = subscription?.specific?.troveId ?? subData.troveId;
  if (isPresent(troveId)) return String(troveId);
  return isPresent(subscription?.owner) ? String(subscription.owner) : undefined;
};

// A missing cap and a sentinel cap both mean "no constraint", so neither gets a number.
const gasPriceSortValue = (value: unknown): AutomationSortValue => {
  if (!isPresent(value) || isMaxUintLike(value)) return undefined;
  const numeric = toNumber(value);
  return numeric === undefined || numeric > NO_GAS_CAP_THRESHOLD ? undefined : numeric;
};

// Sorts by the number the column actually shows, inversion included.
const pairPriceSortValue = (
  pair: StablePricePair,
  price: unknown,
): AutomationSortValue => toNumber(priceOnPair(pair, price));

const column = (
  key: string,
  label: string,
  numeric = false,
  sortable = true,
): AutomationStrategyColumn => ({
  key,
  label,
  ...(numeric ? { numeric: true } : {}),
  ...(sortable ? { sortable: true } : {}),
});

const COMMON_LENDING_COLUMNS = [
  column('position', 'Position'),
  column('supplied', 'Supplied', true),
  column('borrowed', 'Borrowed', true),
  column('currentRatio', 'Current ratio', true),
];

const END_COLUMNS = [
  column('protocol', 'Protocol'),
  column('network', 'Network'),
];

const LEVERAGE_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('repay', 'Repay', true),
  column('boost', 'Boost', true),
  ...END_COLUMNS,
];

const LIQUIDATION_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('repayTrigger', 'Repay trigger', true),
  column('targetRatio', 'Target ratio', true),
  column('status', 'Status'),
  ...END_COLUMNS,
];

const REPAY_PRICE_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('repayCondition', 'Repay condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  column('targetRatio', 'Target ratio', true),
  ...END_COLUMNS,
];

const BOOST_PRICE_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('boostCondition', 'Boost condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  column('targetRatio', 'Target ratio', true),
  ...END_COLUMNS,
];

const OPEN_ORDER_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('openCondition', 'Open condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  column('targetRatio', 'Target ratio', true),
  ...END_COLUMNS,
];

const PAYBACK_COLUMNS = [
  column('position', 'Trove / Position'),
  column('supplied', 'Supplied', true),
  column('borrowed', 'Borrowed', true),
  column('currentRatio', 'Current ratio', true),
  column('paybackTrigger', 'Payback trigger', true),
  column('targetRatio', 'Target ratio', true),
  column('status', 'Status'),
  ...END_COLUMNS,
];

const CLOSE_PRICE_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('stopLoss', 'Stop loss', true),
  column('takeProfit', 'Take profit', true),
  column('changeToTrigger', 'Change to trigger', true),
  ...END_COLUMNS,
];

const STOP_LOSS_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('stopLossCondition', 'Stop-loss condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  ...END_COLUMNS,
];

const STOP_LOSS_GAS_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('stopLossCondition', 'Stop-loss condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  column('gasPriceCondition', 'Gas-price condition', true),
  ...END_COLUMNS,
];

const TAKE_PROFIT_GAS_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('pricePair', 'Price pair'),
  column('currentPrice', 'Current price', true),
  column('takeProfitCondition', 'Take-profit condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  column('gasPriceCondition', 'Gas-price condition', true),
  ...END_COLUMNS,
];

const TAKE_PROFIT_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('triggerAsset', 'Trigger asset / pair'),
  column('currentPrice', 'Current price', true),
  column('takeProfitCondition', 'Take-profit condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  ...END_COLUMNS,
];

const COLLATERAL_SWITCH_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('switch', 'Switch'),
  column('amount', 'Amount', true),
  column('currentPrice', 'Current price', true),
  column('priceCondition', 'Price condition', true),
  column('changeToTrigger', 'Change to trigger', true),
  ...END_COLUMNS,
];

const DCA_COLUMNS = [
  column('position', 'Position'),
  column('spendPerSwap', 'Spend per swap', true),
  column('buyAsset', 'Buy asset'),
  column('perSwapValue', 'Per-swap value', true),
  column('frequency', 'Frequency'),
  column('scheduleAnchor', 'Schedule anchor'),
  column('nextCadence', 'Next cadence'),
  ...END_COLUMNS,
];

const LIMIT_ORDER_COLUMNS = [
  column('position', 'Position'),
  column('orderType', 'Order type'),
  column('sell', 'Sell', true),
  column('receive', 'Receive'),
  column('currentPrice', 'Current price', true),
  column('targetPrice', 'Target price', true),
  column('changeToTrigger', 'Change to trigger', true),
  column('orderValue', 'Order value', true),
  column('expires', 'Expires'),
  ...END_COLUMNS,
];

const DEBT_IN_FRONT_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('debtInFrontThreshold', 'Debt-in-front threshold', true),
  column('ratioIncrease', 'Ratio increase', true),
  ...END_COLUMNS,
];

const UNKNOWN_COLUMNS = [
  ...COMMON_LENDING_COLUMNS,
  column('configuration', 'Configuration', false, false),
  ...END_COLUMNS,
];

export const getAutomationStrategyColumns = (strategyId: string): AutomationStrategyColumn[] => {
  switch (strategyId) {
    case 'legacy':
    case 'leverage-management':
    case 'leverage-management-eoa':
      return LEVERAGE_COLUMNS;
    case 'liquidation-protection':
    case 'liquidation-protection-eoa':
      return LIQUIDATION_COLUMNS;
    case 'repay-on-price':
    case 'eoa-repay-on-price':
      return REPAY_PRICE_COLUMNS;
    case 'boost-on-price':
      return BOOST_PRICE_COLUMNS;
    case 'open-order-from-collateral':
      return OPEN_ORDER_COLUMNS;
    case 'payback':
      return PAYBACK_COLUMNS;
    case 'close-on-price':
    case 'eoa-close-on-price':
      return CLOSE_PRICE_COLUMNS;
    case 'stop-loss':
      return STOP_LOSS_COLUMNS;
    case 'stop-loss-with-gas-price':
      return STOP_LOSS_GAS_COLUMNS;
    case 'take-profit-with-gas-price':
      return TAKE_PROFIT_GAS_COLUMNS;
    case 'take-profit':
      return TAKE_PROFIT_COLUMNS;
    case 'collateral-switch':
      return COLLATERAL_SWITCH_COLUMNS;
    case 'dca':
      return DCA_COLUMNS;
    case 'limit-order':
      return LIMIT_ORDER_COLUMNS;
    case 'debt-in-front-repay':
      return DEBT_IN_FRONT_COLUMNS;
    default:
      return UNKNOWN_COLUMNS;
  }
};

const addLendingCells = (
  cells: Record<string, AutomationTableLine[]>,
  sortValues: Record<string, AutomationSortValue>,
  subscription: any,
  suppliedAssets: AutomationPositionAsset[],
  borrowedAssets: AutomationPositionAsset[],
) => {
  const suppliedTotal = subscription?.positionInfo?.suppliedCollateralUsd;
  const borrowedTotal = subscription?.positionInfo?.borrowedUsd;
  const ratio = toNumber(subscription?.positionInfo?.ratio);
  /* eslint-disable no-param-reassign */
  cells.supplied = assetLines(suppliedAssets, suppliedTotal, '\u2014', 'Total supplied');
  cells.borrowed = assetLines(borrowedAssets, borrowedTotal, 'No debt', 'Total borrowed');
  cells.currentRatio = currentRatioLines(subscription);
  sortValues.supplied = toNumber(suppliedTotal);
  sortValues.borrowed = toNumber(borrowedTotal);
  // A zero or absent ratio means "not applicable", not "closest to liquidation".
  sortValues.currentRatio = ratio !== undefined && ratio > 0 ? ratio : undefined;
  /* eslint-enable no-param-reassign */
};

const leverageConditionLines = (
  subscription: any,
  enabled: boolean,
  trigger: unknown,
  target: unknown,
  direction: 'under' | 'over',
): AutomationTableLine[] => {
  if (!enabled) return [{ text: 'Disabled', tone: 'disabled' }];
  const state = statusForRatio(subscription?.positionInfo?.ratio, trigger, direction);
  const lines: AutomationTableLine[] = [
    { text: `${direction === 'under' ? '\u2264' : '\u2265'} ${formatRatio(trigger)}`, meta: 'Trigger' },
    { text: formatRatio(target), meta: 'Target' },
  ];
  if (state.tone !== 'safe') lines.push({ text: state.text, meta: state.meta, tone: state.tone });
  return lines;
};

export const formatAutomationStrategyRow = (
  subscription: any,
  nowMs: number = Date.now(),
  priceIndex: TokenPriceIndex = {},
): AutomationStrategyRow => {
  const strategyId = String(subscription?.strategy?.strategyId || 'unknown');
  const specific = subscription?.specific || {};
  const subData = subscription?.strategyData?.decoded?.subData || {};
  const triggerData = subscription?.strategyData?.decoded?.triggerData || {};
  const positionInfo = subscription?.positionInfo || {};
  const suppliedAssets = getPositionAssets(subscription, 'supplied');
  const borrowedAssets = getPositionAssets(subscription, 'borrowed');
  const tokenMap = buildTokenMap(subscription, suppliedAssets, borrowedAssets);
  const networkCells = networkLines(subscription);
  const cells: Record<string, AutomationTableLine[]> = {
    position: identityLines(subscription),
    protocol: protocolLines(subscription),
    network: networkCells,
  };
  const sortValues: Record<string, AutomationSortValue> = {
    position: identitySortValue(subscription),
    protocol: subscription?.protocol?.fullName || subscription?.protocol?.name || undefined,
    network: toNumber(subscription?.chainId) === undefined ? undefined : networkCells[0].text,
  };

  if (strategyId !== 'dca' && strategyId !== 'limit-order') {
    addLendingCells(cells, sortValues, subscription, suppliedAssets, borrowedAssets);
  }

  switch (strategyId) {
    case 'legacy':
    case 'leverage-management':
    case 'leverage-management-eoa': {
      cells.repay = leverageConditionLines(
        subscription,
        Boolean(specific.repayEnabled),
        specific.triggerRepayRatio,
        specific.targetRepayRatio,
        'under',
      );
      cells.boost = leverageConditionLines(
        subscription,
        Boolean(specific.boostEnabled),
        specific.triggerBoostRatio,
        specific.targetBoostRatio,
        'over',
      );
      sortValues.repay = specific.repayEnabled ? toNumber(specific.triggerRepayRatio) : undefined;
      sortValues.boost = specific.boostEnabled ? toNumber(specific.triggerBoostRatio) : undefined;
      break;
    }

    case 'liquidation-protection':
    case 'liquidation-protection-eoa': {
      const trigger = specific.triggerRepayRatio;
      const state = statusForRatio(positionInfo.ratio, trigger, 'under');
      cells.repayTrigger = [{ text: `\u2264 ${formatRatio(trigger)}` }];
      cells.targetRatio = [{ text: formatRatio(specific.targetRepayRatio) }];
      cells.status = [{ text: state.text, meta: state.meta, tone: state.tone }];
      sortValues.repayTrigger = toNumber(trigger);
      sortValues.targetRatio = toNumber(specific.targetRepayRatio);
      sortValues.status = state.text;
      break;
    }

    case 'repay-on-price':
    case 'eoa-repay-on-price': {
      const base = resolveToken(tokenMap, specific.baseToken || triggerData.baseTokenAddress, undefined, subscription?.chainId);
      const quote = resolveToken(tokenMap, specific.quoteToken || triggerData.quoteTokenAddress, undefined, subscription?.chainId);
      const currentPrice = nonStableAssetPrice(
        suppliedAssets,
        borrowedAssets,
        positionInfo.suppliedCollateralUsd,
        positionInfo.borrowedUsd,
      );
      const pair = stablePair(base, quote);
      cells.pricePair = [{ text: pairLabel(pair) }];
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      const repayPrice = specific.price ?? triggerData.price;
      const repayState = specific.ratioState ?? triggerData.ratioState;
      cells.repayCondition = [pairConditionLine(pair, repayPrice, repayState)];
      cells.targetRatio = [{ text: formatRatio(specific.ratio ?? subData.targetRatio) }];
      sortValues.pricePair = pairLabel(pair);
      sortValues.currentPrice = currentPrice;
      sortValues.repayCondition = pairPriceSortValue(pair, repayPrice);
      applyChangeToTrigger(cells, sortValues, [changeToTrigger(
        currentPrice,
        priceOnPair(pair, repayPrice),
        stateOnPair(pair, repayState),
      )]);
      sortValues.targetRatio = toNumber(specific.ratio ?? subData.targetRatio);
      break;
    }

    case 'boost-on-price': {
      const collateral = resolveToken(tokenMap, specific.collToken || triggerData.collToken, 'collateral', subscription?.chainId);
      const base = resolveToken(tokenMap, specific.baseToken || subData.baseToken, 'base token', subscription?.chainId);
      const currentPrice = nonStableAssetPrice(
        suppliedAssets,
        borrowedAssets,
        positionInfo.suppliedCollateralUsd,
        positionInfo.borrowedUsd,
      );
      const pair = stablePair(collateral, base);
      const boostPrice = specific.price ?? triggerData.price;
      const boostState = specific.priceState ?? triggerData.priceState;
      cells.pricePair = [{ text: pairLabel(pair) }];
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      cells.boostCondition = [pairConditionLine(pair, boostPrice, boostState)];
      cells.targetRatio = [{ text: formatRatio(specific.ratio ?? subData.targetRatio) }];
      sortValues.pricePair = pairLabel(pair);
      sortValues.currentPrice = currentPrice;
      sortValues.boostCondition = pairPriceSortValue(pair, boostPrice);
      applyChangeToTrigger(cells, sortValues, [changeToTrigger(
        currentPrice,
        priceOnPair(pair, boostPrice),
        stateOnPair(pair, boostState),
      )]);
      sortValues.targetRatio = toNumber(specific.ratio ?? subData.targetRatio);
      break;
    }

    case 'open-order-from-collateral': {
      const base = resolveToken(tokenMap, specific.baseToken || triggerData.baseTokenAddress, undefined, subscription?.chainId);
      const quote = resolveToken(tokenMap, specific.quoteToken || triggerData.quoteTokenAddress, undefined, subscription?.chainId);
      const currentPrice = nonStableAssetPrice(
        suppliedAssets,
        borrowedAssets,
        positionInfo.suppliedCollateralUsd,
        positionInfo.borrowedUsd,
      );
      const pair = stablePair(base, quote);
      cells.pricePair = [{ text: pairLabel(pair) }];
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      const openPrice = specific.price ?? triggerData.price;
      const openState = specific.ratioState ?? triggerData.ratioState;
      cells.openCondition = [
        pairConditionLine(pair, openPrice, openState, 'Opens from collateral'),
      ];
      cells.targetRatio = [{ text: formatRatio(specific.ratio ?? subData.targetRatio) }];
      sortValues.pricePair = pairLabel(pair);
      sortValues.currentPrice = currentPrice;
      sortValues.openCondition = pairPriceSortValue(pair, openPrice);
      applyChangeToTrigger(cells, sortValues, [changeToTrigger(
        currentPrice,
        priceOnPair(pair, openPrice),
        stateOnPair(pair, openState),
      )]);
      sortValues.targetRatio = toNumber(specific.ratio ?? subData.targetRatio);
      break;
    }

    case 'payback': {
      const trigger = specific.triggerRatio ?? triggerData.ratio;
      const state = statusForRatio(positionInfo.ratio, trigger, 'under');
      cells.paybackTrigger = [{ text: `\u2264 ${formatRatio(trigger)}` }];
      cells.targetRatio = [{ text: formatRatio(specific.targetRatio ?? subData.targetRatio) }];
      cells.status = [{ text: state.text, meta: state.meta, tone: state.tone }];
      sortValues.paybackTrigger = toNumber(trigger);
      sortValues.targetRatio = toNumber(specific.targetRatio ?? subData.targetRatio);
      sortValues.status = state.text;
      break;
    }

    case 'close-on-price':
    case 'eoa-close-on-price': {
      const destinations = closeTypes(subData.closeType);
      const explicitPair = isPresent(specific.quoteToken);
      const collateral = resolveToken(
        tokenMap,
        explicitPair
          ? specific.baseToken
          : (specific.collToken || specific.collAsset || subData.collToken || subData.collAsset),
        'collateral',
        subscription?.chainId,
      );
      const quote = resolveToken(
        tokenMap,
        explicitPair
          ? specific.quoteToken
          : (specific.baseToken || specific.debtAsset || subData.debtAsset),
        'debt',
        subscription?.chainId,
      );
      const stopLossPrice = specific.stopLossPrice ?? triggerData.lowerPrice;
      const takeProfitPrice = specific.takeProfitPrice ?? triggerData.upperPrice;
      const currentPrice = nonStableAssetPrice(
        suppliedAssets,
        borrowedAssets,
        positionInfo.suppliedCollateralUsd,
        positionInfo.borrowedUsd,
      );
      const pair = stablePair(collateral, quote);
      // Inverting the pair swaps which side of the threshold each close sits on.
      const stopLossDirection = pair.inverted ? 'At or above' : 'At or below';
      const takeProfitDirection = pair.inverted ? 'At or below' : 'At or above';
      cells.pricePair = [{ text: pairLabel(pair) }];
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      cells.stopLoss = isConfiguredPrice(stopLossPrice)
        ? [{
          text: `${stopLossDirection} ${formatPrice(priceOnPair(pair, stopLossPrice))}`,
          meta: `Close to ${isPresent(specific.stopLossType) ? closeDestination(specific.stopLossType) : (destinations.stopLoss || 'destination not returned')}`,
        }]
        : [{ text: 'Not configured', tone: 'disabled' }];
      cells.takeProfit = isConfiguredPrice(takeProfitPrice)
        ? [{
          text: `${takeProfitDirection} ${formatPrice(priceOnPair(pair, takeProfitPrice))}`,
          meta: `Close to ${isPresent(specific.takeProfitType) ? closeDestination(specific.takeProfitType) : (destinations.takeProfit || 'destination not returned')}`,
        }]
        : [{ text: 'Not configured', tone: 'disabled' }];
      sortValues.pricePair = pairLabel(pair);
      sortValues.currentPrice = currentPrice;
      sortValues.stopLoss = isConfiguredPrice(stopLossPrice)
        ? pairPriceSortValue(pair, stopLossPrice)
        : undefined;
      sortValues.takeProfit = isConfiguredPrice(takeProfitPrice)
        ? pairPriceSortValue(pair, takeProfitPrice)
        : undefined;
      applyChangeToTrigger(cells, sortValues, [
        changeToTrigger(
          currentPrice,
          isConfiguredPrice(stopLossPrice) ? priceOnPair(pair, stopLossPrice) : undefined,
          pair.inverted ? 0 : 1,
          'Stop loss',
        ),
        changeToTrigger(
          currentPrice,
          isConfiguredPrice(takeProfitPrice) ? priceOnPair(pair, takeProfitPrice) : undefined,
          pair.inverted ? 1 : 0,
          'Take profit',
        ),
      ]);
      break;
    }

    case 'stop-loss':
    case 'stop-loss-with-gas-price':
    case 'take-profit-with-gas-price': {
      const base = resolveToken(tokenMap, specific.baseToken || triggerData.baseTokenAddress, undefined, subscription?.chainId);
      const quote = resolveToken(tokenMap, specific.quoteToken || triggerData.quoteTokenAddress, undefined, subscription?.chainId);
      const isStopLoss = strategyId.startsWith('stop-loss');
      const state = specific.ratioState ?? triggerData.ratioState;
      const triggerPrice = specific.price ?? triggerData.price;
      const pair = stablePair(base, quote);
      const condition = pairConditionLine(pair, triggerPrice, state);
      // Stop-loss protects a long against falling prices ("At or below", state 1) and a short
      // against rising prices ("At or above", state 0) - the opposite of take-profit's mapping.
      const currentPrice = currentAssetPrice(
        isStopLoss ? (Number(state) === 0 ? 1 : 0) : state,
        suppliedAssets,
        borrowedAssets,
        positionInfo.suppliedCollateralUsd,
        positionInfo.borrowedUsd,
      );
      cells.pricePair = [{ text: pairLabel(pair) }];
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      sortValues.pricePair = pairLabel(pair);
      sortValues.currentPrice = currentPrice;
      if (isStopLoss) {
        cells.stopLossCondition = [condition];
        sortValues.stopLossCondition = pairPriceSortValue(pair, triggerPrice);
      } else {
        cells.takeProfitCondition = [condition];
        sortValues.takeProfitCondition = pairPriceSortValue(pair, triggerPrice);
      }
      applyChangeToTrigger(cells, sortValues, [changeToTrigger(
        currentPrice,
        priceOnPair(pair, triggerPrice),
        stateOnPair(pair, state),
      )]);
      if (strategyId !== 'stop-loss') {
        const maximumGasPrice = specific.maximumGasPrice ?? triggerData.maximumGasPrice;
        cells.gasPriceCondition = [gasPriceLine(maximumGasPrice)];
        sortValues.gasPriceCondition = gasPriceSortValue(maximumGasPrice);
      }
      break;
    }

    case 'take-profit': {
      const hasPair = isPresent(specific.baseToken)
        || isPresent(specific.quoteToken)
        || isPresent(triggerData.baseTokenAddress)
        || isPresent(triggerData.quoteTokenAddress);
      if (hasPair) {
        const base = resolveToken(tokenMap, specific.baseToken || triggerData.baseTokenAddress, undefined, subscription?.chainId);
        const quote = resolveToken(tokenMap, specific.quoteToken || triggerData.quoteTokenAddress, undefined, subscription?.chainId);
        const state = specific.ratioState ?? triggerData.ratioState;
        const triggerPrice = specific.price ?? triggerData.price;
        const currentPrice = currentAssetPrice(
          state,
          suppliedAssets,
          borrowedAssets,
          positionInfo.suppliedCollateralUsd,
          positionInfo.borrowedUsd,
        );
        const pair = stablePair(base, quote);
        cells.triggerAsset = [{ text: pairLabel(pair) }];
        cells.currentPrice = [{ text: formatPrice(currentPrice) }];
        cells.takeProfitCondition = [pairConditionLine(pair, triggerPrice, state)];
        sortValues.triggerAsset = pairLabel(pair);
        sortValues.currentPrice = currentPrice;
        sortValues.takeProfitCondition = pairPriceSortValue(pair, triggerPrice);
        applyChangeToTrigger(cells, sortValues, [changeToTrigger(
          currentPrice,
          priceOnPair(pair, triggerPrice),
          stateOnPair(pair, state),
        )]);
      } else {
        const triggerAsset = resolveToken(
          tokenMap,
          triggerData.tokenAddr || specific.closeToAssetAddr,
          positionInfo.asset || 'asset',
          subscription?.chainId,
        );
        const state = triggerData.state ?? specific.ratioState ?? 0;
        const triggerPrice = specific.price ?? triggerData.price;
        const currentPrice = currentAssetPrice(
          state,
          suppliedAssets,
          borrowedAssets,
          positionInfo.suppliedCollateralUsd,
          positionInfo.borrowedUsd,
        );
        cells.triggerAsset = [{ text: triggerAsset }];
        cells.currentPrice = [{ text: formatPrice(currentPrice) }];
        cells.takeProfitCondition = [priceConditionLine(triggerPrice, state)];
        sortValues.triggerAsset = triggerAsset;
        sortValues.currentPrice = currentPrice;
        sortValues.takeProfitCondition = toNumber(triggerPrice);
        applyChangeToTrigger(cells, sortValues, [
          changeToTrigger(currentPrice, triggerPrice, state),
        ]);
      }
      break;
    }

    case 'collateral-switch': {
      const from = positionInfo.fromToken || resolveAssetReference(
        subscription,
        tokenMap,
        subData.fromAsset,
        subData.fromAssetId,
        'source token',
      );
      const to = positionInfo.toToken || resolveAssetReference(
        subscription,
        tokenMap,
        subData.toAsset,
        subData.toAssetId,
        'destination token',
      );
      const base = resolveAssetReference(
        subscription,
        tokenMap,
        triggerData.baseTokenAddress,
        triggerData.baseTokenId,
        to,
      );
      const quote = resolveAssetReference(
        subscription,
        tokenMap,
        triggerData.quoteTokenAddress,
        triggerData.quoteTokenId,
        from,
      );
      const rawAmount = subData.amountToSwitch;
      const all = isMaxUintLike(rawAmount);
      const fromAssetInfo = isAddress(subData.fromAsset)
        ? getAssetInfoByAddress(subData.fromAsset, toNumber(subscription?.chainId))
        : undefined;
      const fromAssetSymbol = fromAssetInfo
        && fromAssetInfo.symbol !== '?'
        && typeof fromAssetInfo.decimals === 'number'
        ? fromAssetInfo.symbol
        : undefined;
      const amountInEth = fromAssetSymbol
        ? assetAmountInEth(String(rawAmount), fromAssetSymbol)
        : undefined;
      const amountLine = fromAssetSymbol
        ? { text: `${formatAmount(amountInEth)} ${fromAssetSymbol}` }
        : { text: `${formatRawInteger(rawAmount)} base units` };
      cells.switch = [{ text: `${from} \u2192 ${to}` }];
      cells.amount = all
        ? [{ text: 'All available collateral' }]
        : [
          { text: 'Configured amount' },
          amountLine,
        ];
      const currentPrice = nonStableAssetPrice(
        suppliedAssets,
        borrowedAssets,
        positionInfo.suppliedCollateralUsd,
        positionInfo.borrowedUsd,
      );
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      const pair = stablePair(base, quote);
      cells.priceCondition = [
        { text: pairLabel(pair), meta: 'Trigger pair' },
        pairConditionLine(pair, triggerData.price, triggerData.ratioState),
      ];
      sortValues.currentPrice = currentPrice;
      sortValues.switch = `${from} \u2192 ${to}`;
      // "All available collateral" is the largest amount this subscription can ever switch.
      sortValues.amount = all
        ? Number.POSITIVE_INFINITY
        : toNumber(amountInEth ?? rawAmount);
      sortValues.priceCondition = pairPriceSortValue(pair, triggerData.price);
      applyChangeToTrigger(cells, sortValues, [changeToTrigger(
        currentPrice,
        priceOnPair(pair, triggerData.price),
        stateOnPair(pair, triggerData.ratioState),
      )]);
      break;
    }

    case 'dca': {
      const fromToken = tokenRef(tokenMap, positionInfo.fromToken, subData.fromToken, 'source token', subscription?.chainId);
      const toToken = tokenRef(tokenMap, positionInfo.toToken, subData.toToken, 'destination token', subscription?.chainId);
      const from = fromToken.symbol;
      const to = toToken.symbol;
      const interval = triggerData.interval ?? subData.interval;
      const cadence = nextCadence(triggerData.timestamp, interval, nowMs);
      const perSwapUsd = toNumber(positionInfo.suppliedCollateralUsd);
      cells.spendPerSwap = [{ text: `${formatAmount(subData.amount)} ${from}`, copy: copyFor(fromToken) }];
      cells.buyAsset = [{ text: to, copy: copyFor(toToken) }];
      cells.perSwapValue = [{
        text: (perSwapUsd || 0) > 0 ? formatUsd(positionInfo.suppliedCollateralUsd) : '\u2014',
      }];
      cells.frequency = [{ text: formatInterval(interval) }];
      cells.scheduleAnchor = [{ text: formatUtcTimestamp(triggerData.timestamp) }];
      cells.nextCadence = [{
        text: cadence === undefined ? '\u2014' : formatUtcTimestamp(cadence),
        meta: 'Derived from schedule',
      }];
      sortValues.spendPerSwap = toNumber(subData.amount);
      sortValues.buyAsset = to;
      sortValues.perSwapValue = (perSwapUsd || 0) > 0 ? perSwapUsd : undefined;
      sortValues.frequency = toNumber(interval);
      sortValues.scheduleAnchor = toNumber(triggerData.timestamp);
      sortValues.nextCadence = cadence;
      break;
    }

    case 'limit-order': {
      const fromToken = tokenRef(tokenMap, positionInfo.fromToken, subData.fromToken, 'source token', subscription?.chainId);
      const toToken = tokenRef(tokenMap, positionInfo.toToken, subData.toToken, 'destination token', subscription?.chainId);
      const from = fromToken.symbol;
      const to = toToken.symbol;
      const goodUntil = toNumber(triggerData.goodUntil);
      const orderType = Number(triggerData.orderType) === 0 ? 'Take-profit' : 'Stop-loss';
      cells.orderType = [{ text: orderType }];
      cells.sell = [{ text: `${formatAmount(subData.amount)} ${from}`, copy: copyFor(fromToken) }];
      cells.receive = [{ text: to, copy: copyFor(toToken) }];
      // A limit order's raw target price is "receive per sell", so selling a stablecoin reads
      // as "0.000013 WBTC per USDT" until the pair is flipped.
      const targetPair = stablePair(from, to, triggerData.targetPrice);
      const targetPriceValue = priceOnPair(targetPair, triggerData.targetPrice);
      // getExchangeData prices only the sell token, so the receive token - and the sell
      // token when it is a stablecoin - is priced from the shared index instead.
      const currentPrice = pairCurrentPrice(
        targetPair,
        nonStableAssetPrice(
          suppliedAssets,
          borrowedAssets,
          positionInfo.suppliedCollateralUsd,
          positionInfo.borrowedUsd,
        ),
        priceIndex,
      );
      cells.currentPrice = [{ text: formatPrice(currentPrice) }];
      cells.targetPrice = [{
        text: `${formatPrice(targetPriceValue)} ${targetPair.quote} per ${targetPair.base}`,
        copy: copyFor(fromToken, toToken),
      }];
      cells.orderValue = [{ text: formatUsd(positionInfo.suppliedCollateralUsd) }];
      cells.expires = goodUntil === undefined
        ? [{ text: 'No expiration returned' }]
        : [
          { text: formatUtcTimestamp(goodUntil) },
          {
            text: goodUntil * 1000 <= nowMs ? 'Expired' : 'Active',
            tone: goodUntil * 1000 <= nowMs ? 'disabled' : 'safe',
          },
        ];
      sortValues.orderType = orderType;
      sortValues.sell = toNumber(subData.amount);
      sortValues.receive = to;
      sortValues.currentPrice = currentPrice;
      sortValues.targetPrice = toNumber(targetPriceValue);
      applyChangeToTrigger(cells, sortValues, [changeToTrigger(
        currentPrice,
        targetPriceValue,
        stateOnPair(targetPair, Number(triggerData.orderType) === 0 ? 0 : 1),
      )]);
      sortValues.orderValue = toNumber(positionInfo.suppliedCollateralUsd);
      sortValues.expires = goodUntil;
      break;
    }

    case 'debt-in-front-repay': {
      const debtSymbol = borrowedAssets[0]?.symbol || 'debt token';
      const minimum = specific.debtInFrontMin ?? triggerData.debtInFrontMin;
      const increase = specific.targetRepayRatioIncrease ?? subData.targetRatioIncrease;
      cells.debtInFrontThreshold = [{
        text: `${formatAmount(minimum)} ${debtSymbol}`,
        meta: 'Repay when debt in front falls below',
      }];
      cells.ratioIncrease = [{ text: `${formatAmount(increase)} pp` }];
      sortValues.debtInFrontThreshold = toNumber(minimum);
      sortValues.ratioIncrease = toNumber(increase);
      break;
    }

    default:
      cells.configuration = [{ text: 'Strategy configuration is not available for display.' }];
  }

  return { cells, sortValues };
};
